import { ToggleGroup } from 'components/ToggleGroup';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import {
  Participation,
  ParticipationMutationOptions,
  ParticipationRating,
  ParticipationUpdateData,
  useCreateEvaluationParticipation,
  useIsAdmin,
  usePayParticipant,
} from '@app/api';
import { Theme } from '@app/theme';
import { SubmitButton } from '@app/core';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { TextArea } from '@app/form';
import { MutationOptions } from 'react-query';

const useStyles = createUseStyles((theme: Theme) => ({
  meetingRatingSelector: {
    marginTop: theme.marginBase * 2,
    marginBottom: theme.marginBase * 2,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.marginBase / 2,
  },
  label: {
    ...theme.fonts.label,
  },
  toggleGroup: {
    height: theme.marginBase * 6,
  },
  textAreaContainer: {
    marginTop: 0,
    marginBottom: 0,
  },
  submitButton: {
    width: 'fit-content',
  },
}));

interface Props {
  participation: Participation;
  mutationOptions?: ParticipationMutationOptions;
}

const initialValues: Values = {
  point: -1,
  value: undefined,
  message: undefined,
};

type Values = {
  point: number;
  value: string | undefined;
  message: string | undefined;
};

const pointValue = {
  [ParticipationRating.NOT_GOOD]: 0,
  [ParticipationRating.OK]: 50,
  [ParticipationRating.VERY_GOOD]: 100,
} as { [key: string]: number };

const validationSchema = Yup.object().shape({
  point: Yup.number().required().min(0).max(100),
  value: Yup.string().required(),
});

export const MeetingRatingSelector = ({
  participation,
  mutationOptions,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { mutateAsync: createEvaluationFeedback } =
    useCreateEvaluationParticipation(participation.id);
  const { mutateAsync: payParticipant } = usePayParticipant(mutationOptions);

  const isAdmin = useIsAdmin();
  const submit = async (values: Values) => {
    if (!isAdmin) {
      await createEvaluationFeedback({
        score: values.point,
        message: values.message,
      });
    }
    await payParticipant({ participation });
  };

  const options = useMemo(() => {
    return Object.values(ParticipationRating).map((name) => {
      return {
        value: name,
        label: t(
          `sessions.details.monitoring.meetingRating.ratingOptions.${name}`,
        ),
      };
    });
  }, [t]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submit}
      validationSchema={isAdmin ? undefined : validationSchema}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <div className={classes.meetingRatingSelector}>
            {!isAdmin && (
              <>
                <div className={classes.labelContainer}>
                  <span className={classes.label}>
                    {t(
                      'sessions.details.monitoring.meetingRating.ratingOptionsLabel',
                    )}
                  </span>
                </div>
                <ToggleGroup
                  options={options}
                  selectedValue={values.value}
                  onChange={(name) => {
                    if (values.value === name) {
                      setFieldValue('value', undefined, false);
                      setFieldValue('point', -1, false);
                    } else {
                      setFieldValue('value', name, false);
                      setFieldValue('point', pointValue[name], false);
                    }
                  }}
                  className={classes.toggleGroup}
                />
                {values.value && (
                  <TextArea
                    containerClass={classes.textAreaContainer}
                    name="message"
                    label={
                      values.value === ParticipationRating.VERY_GOOD
                        ? t(
                            'sessions.details.monitoring.meetingRating.helpGood',
                          )
                        : t(
                            'sessions.details.monitoring.meetingRating.helpNotOk',
                          )
                    }
                  />
                )}
              </>
            )}
            <SubmitButton
              className={classes.submitButton}
              disabled={isAdmin ? false : !values.value}
              text={t('sessions.details.monitoring.meetingRating.submitRating')}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};
